import { Injectable } from '@angular/core';
import { isNullOrUndefined } from 'util';
import * as L from 'leaflet';
import { DEFAULT_CORRELATION_LAYER, GURUGRAM_DISTRICT_UUID, GURUGRAM_DISTRICT_NAME, UhiOverlayLayerConfig, URBAN_CORRELATION_LAYERS, URBAN_HEAT_ISLAND_LAYER_LIST, URBAN_HEAT_ISLAND_LEGEND_LIST } from './urban-heat-island-map-constants';
import { AgricultureMapConstants, LAYER_TYPES } from 'src/app/main/constants/dims-map-constants';
import { UrbanHeatIslandDashboardService } from '../urban-heat-island-dashboard.service';
import { environment } from 'src/environments/environment';
import { GisdataService } from 'src/app/main/services/gisdata.service';
import { HttpClient } from '@angular/common/http';
import { UtilsService } from 'src/app/utils/utils.service';



@Injectable({
  providedIn: 'root'
})

export class UrbanHeatIslandMapService {
  selectedLocType: any = null;
  private wardBoundaryLayers: any[] = [];
  private gurugramBoundsCache: any = null;
  private wardBoundsCache: Map<string, any> = new Map();
  private wardPopulationCache: Map<string, number> = new Map();
  // Index-aligned with the component's leafletMaps — whichever single "Layers" dropdown overlay
  // (if any) is currently active. Kept separate from wardBoundaryLayers/panelOverlays since it's
  // untouched by raster-panel rebuilds, ward-boundary re-fetches, play-bar ticks, and fullscreen
  // toggling.
  private activeOverlayLayers: any[] = [];


  // Timeline-aware correlation rasters, mirroring the Satellite (Cloud Top Temperature) module's
  // loadGeoTiffLayer()/geoTiffLayers pattern: one tile layer per (correlation type, timeline date),
  // all pre-loaded onto the map and toggled via CSS display — never removed/recreated per tick.
  geoTiffLayers: Map<string, Map<string, any>> = new Map();
  geoTiffOverlayers: Map<string, Map<string, any>> = new Map();
  activeGeoTiffDateKey: string = null;
  activeMapInstance: any = null;
  selectedCorrelationType: string = DEFAULT_CORRELATION_LAYER;

  // Timeline play bar state. This service is a root singleton, so the correlationCache$/
  // activeTimelineDate$ subscriptions are set up exactly once and simply read whichever
  // gisMapComp last called getSrcWiseLayers — never re-subscribed per call.
  activeGisMapComp: any = null;
  timelineSubscribed: boolean = false;

  // Tracks the floating legend's open/closed state across rebuilds — createLegend()/addLegend()
  // (shared by every raster module in gis-dims-map.component.ts) always recreates the Leaflet
  // legend control from scratch and force-expands it on every call, with no built-in memory of
  // the user's last choice. Restoring it here (via the control's own public collapseContainer()/
  // expandContainer(), see restoreLegendToggleState()) is what lets the "L" toggle survive a
  // location reload (drill-down/back-navigation) or a raster-type switch, without forking the
  // shared legend mechanism other modules (Satellite, Nowcast, Rainfall) also rely on.
  legendCollapsed: boolean = false;

  constructor(
    private urbanHeatIslandDashSrv: UrbanHeatIslandDashboardService,
    private gisdataService: GisdataService,
    private http: HttpClient,
    private gisUtils: UtilsService   // needed so uhiGisComp.gisUtils.setSelectedParams() works for map-click drilldown
  ) { }

  private ensurePane(map: any, paneName: string) {
    if (paneName && !map.getPane(paneName)) {
      const pane = map.createPane(paneName);
      pane.style.zIndex = '500';
    }
  }

  applyWardBoundary(maps: any[], queryParams?:any) {
    this.clearWardBoundary(maps);

    const rawDef = URBAN_HEAT_ISLAND_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['Ward Boundary'];
    if (!rawDef) { return; }

    // Mirrors what the generic GisDimsMapComponent base class sets on the real gisMapComp for
    // the old single-view flow: componentType = 'uhi' (matched by setVECTOROverlays' hover
    // branch) and injectedService = the UrbanHeatIslandMapService instance itself (matched by
    // otherOptions.serviceType in the old otherOptions config). Without both of these, the
    // 'uhi' hover branch in setVECTOROverlays is skipped entirely — this is why hover has been
    // silently missing since the 4-panel rewrite.
    const isDistrictLevel = !queryParams || queryParams.locType === 'DISTRICT';
    const uhiGisComp = {
      otherOptions: { onLocationClick: isDistrictLevel },
      params: { component: '' },
      componentType: 'uhi',
      injectedService: this,
      gisUtils: this.gisUtils
    };

    // At WARD level, scope the boundary layer down to just the selected ward (via the same
    // uuid= cql_filter pattern used elsewhere for state_uuid/dist_uuid) so only that ward's
    // outline renders — every other ward's boundary is filtered out server-side. Gurgaon
    // (DISTRICT) level keeps showing every ward, unfiltered, as before.
    const wardCqlFilter = (!isDistrictLevel && queryParams && queryParams.pUUID)
      ? `uuid='${queryParams.pUUID}'`
      : '1=1';

    maps.forEach((map, idx) => {
      if (!map) { return; }
      // this.ensurePane(map, rawDef.layeroptions_pane);

      const wardVectorDefs = {
        // 'Ward Boundary': { ...rawDef, cql_filter: '1=1' }
        'Ward Boundary': { ...rawDef, cql_filter: wardCqlFilter }
      };
      const wardOverlays = this.gisdataService.getApplicationBasedLayer(wardVectorDefs, LAYER_TYPES.VECTOR_LAYER);
      if (!wardOverlays || !wardOverlays['Ward Boundary']) { return; }

      const geoLayerName = wardOverlays['Ward Boundary'].layerOptions.layerName
        ? wardOverlays['Ward Boundary'].layerOptions.layerName.split(':')[1]
        : null;
      if (geoLayerName) {
        wardOverlays['Ward Boundary'].layerOptions.vectorTileLayerStyles = {};
        wardOverlays['Ward Boundary'].layerOptions.vectorTileLayerStyles[geoLayerName] = () => ({
          color: '#000000',
          fill: false,
          weight: 1,
          opacity: 0.9,
          fillOpacity: 0
        });
      }

      const result = this.gisdataService.setVECTOROverlays(
        map, wardOverlays, null, {}, { location: GURUGRAM_DISTRICT_NAME + '##' + GURUGRAM_DISTRICT_UUID, locType: 'DISTRICT' }, null, uhiGisComp
      );
      const layers = Object.values(result);
      this.wardBoundaryLayers[idx] = layers.length ? layers[0] : null;
    });
  }

  // Bumped by every removeOverlayLayer/addOverlayLayer call — captured as a snapshot before a
  // vector overlay's async WFS fetch and re-checked when it resolves, so an out-of-order response
  // (user switched overlay or ward again before the first fetch finished) can never add a stale/
  // duplicate layer on top of whatever's actually selected by the time it arrives.
  private overlayRequestToken = 0;

  /** "Layers" dropdown overlay. Built directly with raw Leaflet primitives (like
   *  buildGeoTiffLayer()) rather than through the GisdataService.getWMSOverLays()/
   *  setVECTOROverlays() layer-config-list flow, which is oriented around the single shared
   *  gisMapComp this module's 4-panel grid doesn't use. Rendered on the 'uhiOverlay' pane
   *  (created in UrbanHeatIslandMapComponent.initMaps()) so it always sits above the Ward
   *  Boundary's 'depth' pane.
   *
   *  Only one overlay is ever active at a time — this always removes whatever's currently
   *  showing before adding the newly selected one, so the component doesn't need to know about
   *  that ordering itself; it just calls this with whichever config the user picked (and, for
   *  vector overlays, the currently selected ward's UUID). Adding a future overlay never touches
   *  this method — only UHI_OVERLAY_LAYERS in urban-heat-island-map-constants.ts changes, plus
   *  its layerType.
   *
   *  gurugram_waterlines (layerType 'WMS') keeps the original unfiltered raster/style tile layer.
   *  Every other overlay (layerType 'VECTOR') is fetched as WFS GetFeature GeoJSON scoped to
   *  `wardUuid` via a ward_uuid CQL filter — the same LOCTYPE_CQLFILTERVALUE.WARD attribute
   *  convention the rest of the app uses for ward-level filtering (dims-map-constants.ts).
   *
   *  At the Gurgaon (DISTRICT) level `wardUuid` is omitted: every overlay is a Gurugram-only
   *  dataset, so the city-wide context is simply the unfiltered layer. Overlays flagged with
   *  cityLevelLayerType 'WMS' (too large for client-side GeoJSON) use the WMS branch there. */
  addOverlayLayer(maps: any[], overlay: UhiOverlayLayerConfig, wardUuid?: string): void {
    this.removeOverlayLayer(maps);

    const isCityLevel = isNullOrUndefined(wardUuid);
    if (overlay.layerType === 'WMS' || (isCityLevel && overlay.cityLevelLayerType === 'WMS')) {
      maps.forEach((map, idx) => {
        if (!map) { return; }
        const layer = L.tileLayer.wms(environment.url.COMMON_GEOSERVER_URL + 'wms', {
          layers: `${overlay.workspace}:${overlay.layer}`,
          format: 'image/png',
          transparent: true,
          version: '1.1.0',
          pane: 'uhiOverlay',
        });
        layer.addTo(map);
        this.activeOverlayLayers[idx] = layer;
      });
      return;
    }

    // Vector overlay — scoped to the selected ward, or the whole (Gurugram-only) layer at the
    // Gurgaon level when no wardUuid is given.
    const requestToken = ++this.overlayRequestToken;
    this.fetchOverlayGeoJSON(overlay, wardUuid).then((geojson: any) => {
      if (requestToken !== this.overlayRequestToken || isNullOrUndefined(geojson)) { return; }
      maps.forEach((map, idx) => {
        if (!map) { return; }
        const layer = L.geoJSON(geojson, {
          pane: 'uhiOverlay',
          style: () => ({ color: '#000', weight: 1, fillColor: '#000000', fillOpacity: 0.35 }),
          pointToLayer: (feature: any, latlng: any) => L.circleMarker(latlng, {
            radius: 5, color: '#000', weight: 1, fillColor: '#000000', fillOpacity: 0.85
          })
        });
        layer.addTo(map);
        this.activeOverlayLayers[idx] = layer;
      });
    });
  }

  private async fetchOverlayGeoJSON(overlay: UhiOverlayLayerConfig, wardUuid?: string): Promise<any> {
    // These utility layers don't carry a 'ward_uuid' attribute (that's the generic
    // LOCTYPE_CQLFILTERVALUE.WARD convention used by dims-map-constants.ts's location-hierarchy
    // layers) — each feature here is instead pre-joined to its containing ward's uuid under the
    // plain 'uuid' property (confirmed against GeoServer's DescribeFeatureType/actual data: the
    // same 'uuid' repeats across every feature within one ward, one distinct value per ward).
    const url = environment.url.COMMON_GEOSERVER_URL
      + 'wfs?service=WFS&version=1.0.0&request=GetFeature'
      + `&typeName=${overlay.workspace}:${overlay.layer}&outputFormat=application/json`
      + (isNullOrUndefined(wardUuid) ? '' : `&cql_filter=uuid='${wardUuid}'`);
    try {
      return await this.http.get(url).toPromise();
    } catch (e) {
      console.warn('UrbanHeatIslandMapService: fetchOverlayGeoJSON failed', overlay.id, e);
      return null;
    }
  }

  /** Always clears any stale per-map reference first so this stays correct across component
   *  remounts (leafletMaps recreated, this service's own activeOverlayLayers array is a
   *  root-singleton and would otherwise hold references to now-removed Leaflet layers/maps).
   *  Also invalidates overlayRequestToken so an in-flight vector fetch from the overlay just
   *  removed can't add itself back after the fact. */
  removeOverlayLayer(maps: any[]): void {
    this.overlayRequestToken++;
    maps.forEach((map, idx) => {
      const layer = this.activeOverlayLayers[idx];
      if (map && layer) {
        try { if (map.hasLayer(layer)) { map.removeLayer(layer); } } catch (_) { }
      }
      this.activeOverlayLayers[idx] = null;
    });
  }

  async fitMapsToGurugram(maps: any[]) {
    if (!this.gurugramBoundsCache) {
      this.gurugramBoundsCache = await this.fetchGurugramBounds();
    }
    if (!this.gurugramBoundsCache) { return; }
    maps.forEach(map => {
      if (!map) { return; }
      try { map.fitBounds(this.gurugramBoundsCache, { padding: [10, 10] }); } catch (_) { }
    });
  }


  /** Fits given maps tightly to a single ward's real boundary — same fitBounds() pattern as
   *  fitMapsToGurugram(), just scoped to one ward's WFS geometry instead of the whole district.
   *  Cached per wardUuid since a ward's boundary never changes across repeat visits. */
  async fitMapsToWard(maps: any[], wardUuid: string) {
    let bounds = this.wardBoundsCache.get(wardUuid);
    if (!bounds) {
      bounds = await this.fetchWardBounds(wardUuid);
      if (bounds) { this.wardBoundsCache.set(wardUuid, bounds); }
    }
    if (!bounds) { return; }
    maps.forEach(map => {
      if (!map) { return; }
      try {
        map.invalidateSize({ animate: false });
        map.fitBounds(bounds, { padding: [10, 10] });
      } catch (_) { }
    });
  }

  private async fetchWardBounds(wardUuid: string): Promise<any> {
    const url = environment.url.COMMON_GEOSERVER_URL
      + 'wfs?service=WFS&version=1.0.0&request=GetFeature'
      + '&typeName=HEATWAVE:gurugram_wards&outputFormat=application/json'
      + `&cql_filter=uuid='${wardUuid}'`;
    try {
      const geojson: any = await this.http.get(url).toPromise();
      const layer = L.geoJSON(geojson);
      const bounds = layer.getBounds();
      return bounds && bounds.isValid() ? bounds : null;
    } catch (e) {
      console.warn('UrbanHeatIslandMapService: fetchWardBounds failed', e);
      return null;
    }
  }

  /** Selected ward's `total_pop` attribute (HEATWAVE:gurugram_wards), for the Population KPI
   *  card — same WFS GetFeature endpoint/cql_filter pattern as fetchWardBounds, just scoped to
   *  one attribute instead of geometry. Cached per wardUuid since it never changes across visits. */
  async getWardPopulation(wardUuid: string): Promise<number> {
    if (isNullOrUndefined(wardUuid)) { return null; }
    if (this.wardPopulationCache.has(wardUuid)) {
      return this.wardPopulationCache.get(wardUuid);
    }
    const population = await this.fetchWardPopulation(wardUuid);
    if (!isNullOrUndefined(population)) {
      this.wardPopulationCache.set(wardUuid, population);
    }
    return population;
  }

  private async fetchWardPopulation(wardUuid: string): Promise<number> {
    const url = environment.url.COMMON_GEOSERVER_URL
      + 'wfs?service=WFS&version=1.0.0&request=GetFeature'
      + '&typeName=HEATWAVE:gurugram_wards&outputFormat=application/json'
      + `&propertyName=total_pop&cql_filter=uuid='${wardUuid}'`;
    try {
      const geojson: any = await this.http.get(url).toPromise();
      const feature = geojson && geojson.features && geojson.features[0];
      const population = feature ? Number(feature.properties['total_pop']) : null;
      return isNullOrUndefined(population) || isNaN(population) ? null : population;
    } catch (e) {
      console.warn('UrbanHeatIslandMapService: fetchWardPopulation failed', e);
      return null;
    }
  }

  private async fetchGurugramBounds(): Promise<any> {
    const url = environment.url.COMMON_GEOSERVER_URL
      + 'wfs?service=WFS&version=1.0.0&request=GetFeature'
      + '&typeName=HEATWAVE:gurugram_boundary&outputFormat=application/json';
    try {
      const geojson: any = await this.http.get(url).toPromise();
      const layer = L.geoJSON(geojson);
      const bounds = layer.getBounds();
      return bounds && bounds.isValid() ? bounds : null;
    } catch (e) {
      console.warn('UrbanHeatIslandMapService: fetchGurugramBounds failed', e);
      return null;
    }
  }

  clearWardBoundary(maps: any[]) {
    this.wardBoundaryLayers.forEach((layer, idx) => {
      const map = maps[idx];
      if (layer && map && map.hasLayer(layer)) { map.removeLayer(layer); }
    });
    this.wardBoundaryLayers = [];
  }

  async getSrcWiseLayers(gisMapComp: any) {
    gisMapComp.legendList = {};
    gisMapComp.showThrobber = true;
    let queryParams: any = gisMapComp['params'];
    gisMapComp.getAndSetBaseLayers(3);

    // GisDimsMapComponent recreates the Leaflet map on every navigation into this module,
    // but this service is a root singleton — cached tile layers from a previous visit are
    // bound to the destroyed map and silently fail to render. Detect a new map and drop them.
    if (this.activeMapInstance !== gisMapComp.map) {
      this.resetCorrelationLayers();
      this.activeMapInstance = gisMapComp.map;
    }

    if (!isNullOrUndefined(queryParams) && Object.keys(queryParams).length > 0) {
      // Urban Heat Island only ever covers a single city (Gurugram) and its wards — unlike the
      // generic COUNTRY/STATE/DISTRICT/BLOCK modules, there's no real hierarchy to index into, so
      // both boundary layers are always shown regardless of locType (including at WARD level).
      let layerList: any = {};
      // layerList['State Boundaries'] = URBAN_HEAT_ISLAND_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['State Boundaries'];
      // layerList['Ward Boundary'] = URBAN_HEAT_ISLAND_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['Ward Boundary'];
      if (queryParams.locType === 'DISTRICT') {
        layerList['State Boundaries'] = URBAN_HEAT_ISLAND_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['State Boundaries'];
        layerList['Ward Boundary'] = URBAN_HEAT_ISLAND_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['Ward Boundary'];
      } else if (queryParams.locType === 'WARD') {
        layerList['Ward Boundaries'] = URBAN_HEAT_ISLAND_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['Ward Boundaries'];

      }

      this.addSrcWiseGeoJSONLayers(gisMapComp, layerList, queryParams);
      this.addSrcWiseVectorLayers(layerList, gisMapComp, queryParams);
      // Correlation raster loading now happens once the timeline dates are known — see
      // setupTimelineSync()'s correlationCache$ subscription below — since each raster is now
      // pre-loaded per timeline date rather than a single type-only layer.
      gisMapComp.showThrobber = false;
    }



    if (!isNullOrUndefined(queryParams) && Object.keys(queryParams).length > 0) {

      let layerList: any = {};


      this.addSrcWiseGeoJSONLayers(gisMapComp, layerList, queryParams);
      this.addSrcWiseVectorLayers(layerList, gisMapComp, queryParams);
      gisMapComp.showThrobber = false;
    }

    // Timeline play bar: fetch once per location context, then let the play bar / slider
    // switch frames purely from the cache (see UrbanHeatIslandDashboardService).
    this.setupTimelineSync(gisMapComp);
    if (!isNullOrUndefined(queryParams) && Object.keys(queryParams).length > 0) {
      this.urbanHeatIslandDashSrv.loadCorrelationData(queryParams);
    }

    // Every location reload (drill-down, back-navigation, initial load) rebuilds the legend
    // control from scratch — re-show whichever correlation type (NDVI/NDBI/LST/LULC) is
    // currently selected, restoring the open/closed toggle state across the rebuild.
    this.updateLegendForType(gisMapComp, this.selectedCorrelationType);
    // addSrcWiseGeoJSONLayers() above resolves its GeoJSON fetch (State/Ward Boundaries)
    // asynchronously and, once it does, gisMapComp itself calls manageLegend() internally
    // (gis-dims-map.component.ts) — whose active-layer scan doesn't know about this raster
    // legend (WARD level's "Ward Boundaries" GeoJSON layer never even registers in
    // gisMapComp.activeLayers, since only vector-tile layers do) and would otherwise clobber
    // it by recreating an empty control. Re-applying once that load has had time to settle
    // keeps this the final, correct legend without forking or awaiting that shared promise.
    setTimeout(() => this.updateLegendForType(gisMapComp, this.selectedCorrelationType), 800);
  }

  // ---- Legend (NDVI/NDBI/LST/LULC), synced to the sidebar's selected correlation type ----

  // Only one legend entry is ever handed to the shared control at a time — bypasses
  // gisMapComp.manageLegend()'s active-layer scan (which ties a legend to a specific vector/
  // GeoJSON layer's legendName) since the legend here tracks the selected RASTER type, not a
  // boundary layer, and calls gisMapComp.addLegend() directly with just that one entry so it's
  // always the only legend visible, matching every raster type change 1:1.
  updateLegendForType(gisMapComp: any, type: string) {
    const legendKey = `${type}_LEGEND`;
    const legendSrc = URBAN_HEAT_ISLAND_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA][legendKey];
    if (isNullOrUndefined(legendSrc) || isNullOrUndefined(gisMapComp) || isNullOrUndefined(gisMapComp.map)) {
      return;
    }
    gisMapComp.legendList[legendKey] = legendSrc;
    gisMapComp.otherOptions.legend = { [legendKey]: legendSrc };
    gisMapComp.addLegend();
    this.restoreLegendToggleState(gisMapComp);
  }

  private restoreLegendToggleState(gisMapComp: any) {
    const legendControl = gisMapComp.htmlNewLegend;
    if (isNullOrUndefined(legendControl)) {
      return;
    }
    // addLegend() always leaves the control expanded — collapse it back if the user had
    // previously closed it, so the "L" toggle's state survives this rebuild.
    if (this.legendCollapsed) {
      legendControl.collapseContainer();
    }
    // The control (and its close/open DOM nodes) is recreated fresh on every rebuild, so these
    // listeners never need unbinding — the old nodes are discarded along with the old control.
    if (legendControl._close) {
      legendControl._close.addEventListener('click', () => { this.legendCollapsed = true; });
    }
    if (legendControl._openContainer) {
      legendControl._openContainer.addEventListener('click', () => { this.legendCollapsed = false; });
    }
  }

  // ---- Timeline play bar (dates from getAggregatedCorrelationData) ----

  setupTimelineSync(gisMapComp: any) {
    this.activeGisMapComp = gisMapComp;
    if (this.timelineSubscribed) {
      return;
    }
    this.timelineSubscribed = true;

    this.urbanHeatIslandDashSrv.correlationCache$.subscribe((cache: any) => {
      if (!this.activeGisMapComp) {
        return;
      }
      const timelineDates: string[] = cache ? cache.timelineDates : [];
      this.activeGisMapComp.labelStrings = timelineDates.map((dateKey: string) => ({
        groupName: dateKey,
        label: {
          label: this.urbanHeatIslandDashSrv.getMonthLabel(dateKey),
          toolTip: this.urbanHeatIslandDashSrv.formatSnapshotDate(dateKey)
        },
        fullLabel: this.urbanHeatIslandDashSrv.formatSnapshotDate(dateKey),
        type: 'forecast',
        // Ticket-mandated GeoTIFF metadata per timeline item, derived from the timeline's own
        // month/date rather than a hardcoded filename — see buildTiffLabel()/buildGeoTiffTileUrl().
        tiffLabel: this.buildTiffLabel(this.selectedCorrelationType, dateKey)
      }));
      this.activeGisMapComp.playButtonDisable = timelineDates.length < 2;
      this.activeGisMapComp.playPosition = timelineDates.length ? timelineDates.length - 1 : 0;

      // A new set of timeline dates means a new location context (drilldown/breadcrumb/search) —
      // rebuild the raster set for the currently selected type against the new dates.
      this.loadGeoTiffLayer(this.activeGisMapComp, this.selectedCorrelationType, timelineDates);
    });

    this.urbanHeatIslandDashSrv.activeTimelineDate$.subscribe((dateKey: any) => {
      if (!this.activeGisMapComp || isNullOrUndefined(dateKey)) {
        return;
      }
      this.activeGisMapComp.activeGroup = dateKey;
      this.toggleGeoTiffVisibility(dateKey);
    });
  }

  // Bound via otherOptions.animationLayers — called by GisDimsMapComponent.changeTimeLayers()
  // whenever the play bar advances (Play) or the user drags the slider/clicks a tick manually.
  // The raster for every timeline date is already pre-loaded onto the map (loadGeoTiffLayer
  // below), so this just flips the shared timeline date; toggleGeoTiffVisibility (triggered by
  // the activeTimelineDate$ subscription above) swaps which raster is visible, and the table/
  // KPIs/charts refresh reactively off that same activeTimelineDate$ — everything one write away.
  changeTimeLayers(gis: any, groupName: any) {
    this.urbanHeatIslandDashSrv.setActiveTimelineDate(groupName);
  }

  // ---- Correlation raster (NDVI / NDBI / LST / LULC), pre-loaded per timeline date ----
  //
  // Mirrors the Satellite (Cloud Top Temperature) module's loadGeoTiffLayer(): one tile layer
  // per timeline date is built and added to the map up front, then play-bar ticks only toggle
  // CSS display between them — no add/remove per tick, so playback never flickers and never
  // re-requests the same tiles twice.
  //
  // Unlike Satellite's cloud_top_temperature layer, these correlation layers are NOT registered
  // as GWC parameter filters, so requesting them through the cached gwc/service/wmts endpoint
  // (Satellite's approach, cql_filter=time='...') silently ignores the filter and always returns
  // the same cached tile — verified by diffing tile bytes across dateKeys, all identical. The
  // GeoServer admin's GetCapabilities screenshot confirms lst/ndbi/ndvi each declare a native WMS
  // <Dimension name="time">; hitting the raw (uncached) /wms GetMap endpoint with the standard
  // WMS `time=YYYY-MM-DD` dimension param DOES return a genuinely different raster per date
  // (verified the same way — tile bytes differ across dateKeys). lulc has no time dimension at
  // all (confirmed absent from GetCapabilities) and stays on a single static tile regardless of
  // the selected date, matching its YEARLY (not DAILY) aggregation elsewhere in this module.
  // MNDWI/MODIS LST Day/MODIS LST Night are DAILY rasters like LST/NDBI/NDVI, so they use the
  // same WMS TIME dimension param.
  private readonly TIME_ENABLED_CORRELATION_TYPES: string[] = [
    'LST', 'NDBI', 'NDVI', 'MNDWI', 'MODIS_LST_DAY', 'MODIS_LST_NIGHT',
    'APPARENT_TEMP', 'AUHI_DAY', 'AUHI_NIGHT', 'HEAT_INDEX',
    'SUHI', 'TAIR_DAY', 'TAIR_NIGHT', 'WET_BULB_TEMP'
  ];

  // The 10 HeatExposureKpis layers (APPARENT_TEMP through WET_BULB_TEMP) are published as
  // monthly composites — the timeline itself still ticks daily, but the WMS TIME dimension for
  // these must always resolve to the 1st of that month, not the actual day.
  private readonly FORCE_MONTH_START_CORRELATION_TYPES: string[] = [
    'APPARENT_TEMP', 'AUHI_DAY', 'AUHI_NIGHT', 'HEAT_INDEX',
    'SUHI', 'TAIR_DAY', 'TAIR_NIGHT', 'WET_BULB_TEMP'
  ];

  // dateKey is 'YYYYMMDD' (e.g. '20260322'); WMS TIME dimension values are 'YYYY-MM-DD'.
  private toWmsTimeValue(dateKey: string, type?: string): string {
    if (isNullOrUndefined(dateKey) || dateKey.length !== 8) {
      return null;
    }
    const day = (type && this.FORCE_MONTH_START_CORRELATION_TYPES.indexOf(type) !== -1)
      ? '01'
      : dateKey.substr(6, 2);
    return `${dateKey.substr(0, 4)}-${dateKey.substr(4, 2)}-${day}`;
  }

  private buildGeoTiffLayer(type: string, dateKey: string): any {
    const layerName = URBAN_CORRELATION_LAYERS[type];
    const wmsOptions: any = {
      layers: layerName,
      format: 'image/png',
      transparent: true,
      version: '1.1.0',
      maxZoom: 22,
      minZoom: 4,
      pane: 'heatMap'
    };
    if (this.TIME_ENABLED_CORRELATION_TYPES.indexOf(type) !== -1) {
      const timeValue = this.toWmsTimeValue(dateKey, type);
      if (timeValue) {
        wmsOptions.time = timeValue;
      }
    }
    // Matches the app-wide WMS convention (see GisDataService.getWMSOverLays/setWMSOverlays): the
    // global geoserver /wms endpoint, no per-workspace path segment, workspace qualified on the
    // `layers` param instead (e.g. 'correlation:lst') — verified this also honors the WMS `time`
    // dimension identically to the workspace-scoped endpoint.
    return L.tileLayer.wms(environment.url.COMMON_GEOSERVER_URL + 'wms', wmsOptions);
  }

  private buildTiffLabel(type: string, dateKey: string): string {
    const month = this.urbanHeatIslandDashSrv.getMonthLabel(dateKey);
    return `${type}_${(month || '').toUpperCase()}_${dateKey}`;
  }

  // Called whenever the timeline dates change (new location context). Wipes every previously
  // pre-loaded raster — old dateKeys no longer correspond to real timeline entries — then builds
  // and shows the given type's layers for the new date list (latest date active by default).
  loadGeoTiffLayer(gisMapComp: any, type: string, timelineDates: string[]) {
    if (isNullOrUndefined(gisMapComp) || isNullOrUndefined(gisMapComp.map)) {
      return;
    }
    this.removeAllGeoTiffLayersFromMap(gisMapComp);
    this.selectedCorrelationType = type;

    if (!timelineDates || !timelineDates.length || isNullOrUndefined(URBAN_CORRELATION_LAYERS[type])) {
      this.activeGeoTiffDateKey = null;
      return;
    }

    const activeDateKey = timelineDates[timelineDates.length - 1];
    this.buildGeoTiffLayersForType(gisMapComp, type, timelineDates);
    this.showTypeLayers(type, activeDateKey);
    this.activeGeoTiffDateKey = activeDateKey;
  }

  // NDVI / NDBI / LST / LULC toggle buttons in the sidebar — switches which correlation type's
  // pre-loaded timeline is visible, keeping the SAME active date (switching raster type shouldn't
  // reset the timeline). Reuses already-built layers for a type visited before; builds fresh
  // (once) the first time a type is selected.
  switchCorrelationLayer(gisMapComp: any, type: string) {
    if (type === this.selectedCorrelationType) {
      return;
    }
    const cache = this.urbanHeatIslandDashSrv.correlationCache$.value;
    const timelineDates: string[] = cache ? cache.timelineDates : [];
    if (!timelineDates.length || isNullOrUndefined(URBAN_CORRELATION_LAYERS[type])) {
      return;
    }
    this.hideTypeLayers(this.selectedCorrelationType);
    this.buildGeoTiffLayersForType(gisMapComp, type, timelineDates);
    const activeDateKey = this.activeGeoTiffDateKey || timelineDates[timelineDates.length - 1];
    this.showTypeLayers(type, activeDateKey);
    this.selectedCorrelationType = type;
    this.activeGeoTiffDateKey = activeDateKey;
    this.updateLegendForType(gisMapComp, type);
  }

  private buildGeoTiffLayersForType(gisMapComp: any, type: string, timelineDates: string[]) {
    if (!this.geoTiffLayers.has(type)) {
      this.geoTiffLayers.set(type, new Map());
    }
    if (!this.geoTiffOverlayers.has(type)) {
      this.geoTiffOverlayers.set(type, new Map());
    }
    const layersForType = this.geoTiffLayers.get(type);
    const overlaysForType = this.geoTiffOverlayers.get(type);

    timelineDates.forEach((dateKey: string) => {
      let tileLayer = layersForType.get(dateKey);
      if (isNullOrUndefined(tileLayer)) {
        tileLayer = this.buildGeoTiffLayer(type, dateKey);
        layersForType.set(dateKey, tileLayer);
      }
      if (!gisMapComp.map.hasLayer(tileLayer)) {
        const addedLayer = tileLayer.addTo(gisMapComp.map);
        overlaysForType.set(dateKey, addedLayer);
      }
    });
  }

  private showTypeLayers(type: string, activeDateKey: string) {
    const overlaysForType = this.geoTiffOverlayers.get(type);
    if (!overlaysForType) {
      return;
    }
    overlaysForType.forEach((layer: any, dateKey: string) => {
      const container = layer && layer.getContainer ? layer.getContainer() : null;
      if (container) {
        container.style.display = dateKey === activeDateKey ? 'block' : 'none';
      }
    });
  }

  private hideTypeLayers(type: string) {
    const overlaysForType = this.geoTiffOverlayers.get(type);
    if (!overlaysForType) {
      return;
    }
    overlaysForType.forEach((layer: any) => {
      const container = layer && layer.getContainer ? layer.getContainer() : null;
      if (container) {
        container.style.display = 'none';
      }
    });
  }

  // Play-bar tick / manual slider click for the CURRENTLY selected correlation type — pure CSS
  // visibility toggle between already-loaded layers, so playback never flickers or re-fetches.
  toggleGeoTiffVisibility(dateKey: string) {
    if (dateKey === this.activeGeoTiffDateKey) {
      return;
    }
    this.showTypeLayers(this.selectedCorrelationType, dateKey);
    this.activeGeoTiffDateKey = dateKey;
  }

  removeAllGeoTiffLayersFromMap(gisMapComp: any) {
    this.geoTiffOverlayers.forEach((datesMap: Map<string, any>) => {
      datesMap.forEach((layer: any) => {
        if (layer && gisMapComp.map.hasLayer(layer)) {
          gisMapComp.map.removeLayer(layer);
        }
      });
    });
    this.geoTiffLayers.clear();
    this.geoTiffOverlayers.clear();
  }

  resetCorrelationLayers() {
    this.geoTiffLayers.clear();
    this.geoTiffOverlayers.clear();
    this.activeGeoTiffDateKey = null;
    this.selectedCorrelationType = DEFAULT_CORRELATION_LAYER;
  }


  addSrcWiseVectorLayers(layerList: any, gisMapComp: any, queryParams: any) {
    let vectorLayersBasedOnView = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.VECTOR_LAYER);
    vectorLayersBasedOnView = this.addSourceWiseLayers(gisMapComp, vectorLayersBasedOnView);

    for (let layer in layerList) {
      if (layerList[layer].applycql_filter) {
        vectorLayersBasedOnView = this.formatVectorLayersData(vectorLayersBasedOnView, queryParams);
      }
    }
    this.setVectorLayerStyles(vectorLayersBasedOnView, gisMapComp);
    gisMapComp.addSrcWiseVECTORLayers(vectorLayersBasedOnView);
  }
  setVectorLayerStyles(layerList: any, gisMapComp: any) {
    for (let key in layerList) {
      let layerName = layerList[key]['layerOptions']['layerName'].split(':')[1];
      layerList[key].layerOptions.vectorTileLayerStyles[layerName] = function (properties: any) {
        return {
          color: '#000',
          fill: false,
          weight: 1,
        };
      };
    }
  }

  addSourceWiseLayers(gisMapComp: any, layerList?: any) {
    let layers: any = {};
    for (let i in layerList) {
      layers[i] = layerList[i];
      gisMapComp.activeLayers[i] = layerList[i].layerOptions.visible;
    }
    return layers;
  }

  formatVectorLayersData(layerList: any, queryParams?: any) {
    if (!isNullOrUndefined(queryParams)) {
      for (let layerName in layerList) {
        let uuid: any = queryParams['pUUID'];
        if (queryParams.locType == 'COUNTRY' && (layerName == 'State Boundary' || layerName == 'District Boundary')) {
          layerList[layerName].layerOptions.cql_filter_value = null;
          layerList[layerName].layerOptions.cql_filter = '1=1';
        }
        if (queryParams.locType == 'STATE' && layerName == 'District Boundary') {
          layerList[layerName].layerOptions.cql_filter_value = 'state_uuid';
          layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + uuid + "'";
        }
        if (queryParams.locType == 'DISTRICT' && layerName == 'Block Boundary') {
          layerList[layerName].layerOptions.cql_filter_value = 'dist_uuid';
          layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + uuid + "'";
        }
      }
      return layerList;
    }
  }

 
  // The raw ward_lgd_n GeoServer attribute is prefixed with the municipal corporation name,
  // e.g. "Gurgaon (M Cl ) - Ward No.29" — display only the "Ward No.29" part everywhere this
  // attribute is shown (hover popup here, and the header pill via updateOptions' UHI branch in
  // gisdata.service.ts) rather than the full raw string. The "No" word itself isn't consistently
  // present in the source data (e.g. ward 34/35 come through as "... - Ward.34", no "No" at all)
  // — matching on just the ward number and rebuilding "Ward No.<n>" normalizes every ward to the
  // same display format regardless of that upstream inconsistency. Falls back to the raw value
  // untouched if no "Ward ... <number>" pattern is found at all, so unexpected formats still display.
  getWardDisplayName(wardLgdName: string): string {
    if (isNullOrUndefined(wardLgdName)) {
      return wardLgdName;
    }
    const match = wardLgdName.match(/Ward[^\d]*(\d+)/i);
    return match ? `Ward No.${match[1]}` : wardLgdName;
  }

  // Hover tooltip for Ward Boundary, mirroring MetwiseAlertsMapService.setHtmlContentForDistrict.
  // Wired up via GisdataService.setVECTOROverlays' componentType == 'uhi' branch, which reads
  // properties already present on the vector-tile feature — no GetFeatureInfo call needed.
  setHtmlContentForWard(properties: any) {
    let html = '<div class="alert-poupup-table">';
    html += `
        <div class="card-body p-0">
            <table class="table table-sm m-0 table-bordered">
                <tbody>
                    <tr>
                        <td>${this.getWardDisplayName(properties['ward_lgd_n'])}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    `;
    html += '</div>';

    return html;
  }

  addLegend(gisMapComp: any) {
    const legendSrc = URBAN_HEAT_ISLAND_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA];
    for (const key in legendSrc) {
      gisMapComp['legendList'][key] = legendSrc[key];
    }
    gisMapComp.manageLegend();
  }

  handleAdminPolygons(layer: any, mapObj: any, params: any, gisMapComp: any) {
    layer.setStyle({ color: '#000000', fill: false, weight: 1.5 });
  }

  // At DISTRICT level, "Ward Boundary" is rendered as a vector tile layer, so its hover popup
  // comes from GisdataService.setVECTOROverlays' componentType == 'uhi' branch (mouseover/mousemove/
  // mouseout bound generically there). At WARD level the layer switches to "Ward Boundaries" — a
  // GeoJSON layer (cql-filtered down to just the selected ward) — which is wired per-feature via
  // onEachFeature instead, so that generic vector-tile hover handler never runs for it. This mirrors
  // the same setHtmlContentForWard popup/pointer-tracking behavior for the GeoJSON case so hovering
  // the selected ward at WARD level shows the identical popup it did at DISTRICT level.
  handleWardBoundaryHover(layer: any, mapObj: any, params: any, gisMapComp: any) {
    // fill: false (as handleAdminPolygons uses) renders SVG fill="none", which SVG hit-testing
    // (pointer-events: visiblePainted) treats as unpainted — only the 1.5px boundary stroke would
    // then register mouseover, not the polygon interior. The district-level vector-tile layer hit-
    // tests its whole polygon regardless of fill (VectorGrid uses its own canvas-based hit-testing),
    // which is why hover felt like it worked anywhere inside a ward there. A non-zero (if tiny)
    // fillOpacity keeps the fill visually invisible while making the whole interior hoverable too,
    // matching that same whole-polygon hover area at WARD level.
    layer.setStyle({ color: '#000000', fill: true, fillOpacity: 0.0001, weight: 1.5 });
    const properties = layer.feature.properties;
    let hoverPopup: any = null;
    layer.on('mouseover', (e: any) => {
      const popup = L.popup({ autoPan: false, minWidth: 100, pane: 'measuretool', maxWidth: 300, closeButton: true });
      popup.setContent(this.setHtmlContentForWard(properties));
      popup.setLatLng(e['latlng']).openOn(mapObj);
      hoverPopup = popup;
    });
    layer.on('mousemove', (e: any) => {
      if (hoverPopup) {
        hoverPopup.setLatLng(e['latlng']);
      }
    });
    layer.on('mouseout', () => {
      mapObj.closePopup();
      hoverPopup = null;
    });
  }

  // Ward Boundary is rendered as its own GeoJSON layer (not routed through the shared
  // gisdataService vector-tile click handler) so ward drilldown stays fully local to this
  // module and can't collide with the generic STATE/DISTRICT/BLOCK click logic other modules rely on.
  handleWardBoundaryClick(layer: any, mapObj: any, params: any, gisMapComp: any) {
    const properties = layer.feature.properties;
    // The GeoJSON onEachFeature wiring in gisdataService.addGeoJsonLayersToMap never actually
    // passes `params` through — it's always undefined here — so read live state off gisMapComp instead.
    const currentParams = gisMapComp.params || {};
    const isSelected = currentParams.locType === 'WARD' && currentParams.pUUID === properties.uuid;
    layer.setStyle({
      color: isSelected ? '#ff5722' : '#000000',
      weight: isSelected ? 2.5 : 1,
      fill: isSelected,
      fillColor: '#ff5722',
      fillOpacity: isSelected ? 0.15 : 0
    });
    layer.on('click', () => {
      const options = { ...gisMapComp.params };
      options['pEUUID'] = GURUGRAM_DISTRICT_UUID;
      options['locType'] = 'WARD';
      options['pType'] = 'WARD';
      options['cType'] = 'WARD';
      options['pUUID'] = properties.uuid;
      options['locName'] = this.getWardDisplayName(properties.ward_lgd_n);
      gisMapComp.gisUtils.setSelectedParams(options);
    });
  }
  addSrcWiseGeoJSONLayers(gisMapComp: any, layerList: any, queryParams?: any) {
    this.selectedLocType = queryParams.locType;
    let geoJsonLayersBasedOnView = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.GEOJSON_LAYER);
    geoJsonLayersBasedOnView = this.formatLayersData(gisMapComp, geoJsonLayersBasedOnView);
    gisMapComp.selectedLocType = null;
    gisMapComp.addSrcWiseGEOJSONLayers(geoJsonLayersBasedOnView, queryParams);
  }

  formatLayersData(gisMapComp: any, layerList: any) {
    for (let layerName in layerList) {
      if (layerList[layerName].layerOptions.cql_filter_value) {
        layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + gisMapComp.params['pUUID'] + "'";
      }
    }
    return layerList;
  }

  // ── ADD to UrbanHeatIslandMapService ──

  /**
   * Multi-map version of buildGeoTiffLayersForType()/loadGeoTiffLayer(): builds every timeline
   * date's tile layer for ONE fixed type directly onto a plain Leaflet map (no gisMapComp needed,
   * since this map is one grid panel, not the shared single-map dashboard). Returns a
   * dateKey -> layer map so the caller (component) can toggle visibility per its own active date,
   * exactly like showTypeLayers()/toggleGeoTiffVisibility() do for the single-map case.
   */
  buildGeoTiffLayersForMap(map: any, type: string, timelineDates: string[]): Map<string, any> {
    const overlays = new Map<string, any>();
    if (!map || !timelineDates || !timelineDates.length || !URBAN_CORRELATION_LAYERS[type]) {
      return overlays;
    }
    timelineDates.forEach((dateKey: string) => {
      const tileLayer = this.buildGeoTiffLayer(type, dateKey); // already public-safe, no gisMapComp dependency
      const added = tileLayer.addTo(map);
      overlays.set(dateKey, added);
    });
    return overlays;
  }

  /** Pure CSS visibility toggle across one panel's pre-loaded dateKey->layer map — mirrors
   *  showTypeLayers()'s display logic but works off the plain Map this component keeps per panel. */
  toggleDateVisibilityOnMap(overlays: Map<string, any>, dateKey: string) {
    if (!overlays) { return; }
    overlays.forEach((layer: any, dk: string) => {
      const container = layer && layer.getContainer ? layer.getContainer() : null;
      if (container) { container.style.display = dk === dateKey ? 'block' : 'none'; }
    });
  }

  /** Removes one panel's full set of pre-loaded raster layers — used when timeline dates change
   *  (new location context) and the panel needs to rebuild from scratch. */
  removeGeoTiffLayersFromMap(map: any, overlays: Map<string, any>) {
    if (!map || !overlays) { return; }
    overlays.forEach((layer: any) => {
      if (layer && map.hasLayer(layer)) { map.removeLayer(layer); }
    });
    overlays.clear();
  }
}


